import React from "react";
import {
	Redirect,
	Route,
	Switch,
	BrowserRouter as Router,
} from "react-router-dom";
import { IonAlert, IonApp, IonRouterOutlet } from "@ionic/react";
import { Capacitor, Plugins } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useTranslation } from "react-i18next";
import Home from "./pages/home";
import CreateSystem from "./pages/create-system";
import EditSystem from "./pages/edit-system";
import System from "./pages/system";
import Settings from "./pages/settings";
import About from "./pages/about";
import Propulsion from "./pages/propulsion";
import TowCable from "./pages/tow-cable";
import ErrorBoundary from "./components/error-boundary";
import Faq from "./pages/faq";

import { useEffect, useState } from "react";
import WithMenu from "./hoc/with-menu";
import { loadLocals } from "./services/init";
import TrawlGates from "./pages/trawl-gates";
import GateSelection from "./pages/trawl-gates/gate-selection";
import ResistanceAndExpansion from "./pages/trawl-gates/resistance-expansion";
import Guinches from "./pages/guinches";
import TrawlSystems from "./pages/trawl-systems";
import Demersals from "./pages/trawl-systems/simple/demersals";
import Crustacean from "./pages/trawl-systems/simple/crustacean";
import TripleTow from "./pages/trawl-systems/double/triple-tow";
import DoubleTow from "./pages/trawl-systems/double/double-tow";
import Tangones from "./pages/trawl-systems/tangones";
import Cloths from "./pages/cloths";
import Pair from "./pages/trawl-systems/pair";
import Flotation from "./pages/flotation";
import LanguagesModal from "./components/modals/languages";
import { WithLoader } from "./hoc/with-loader";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";

const { SplashScreen } = Plugins;

export const LocalizationContext = React.createContext({});

const App: React.FC = () => {
	const exitApp = () => CapApp.exitApp();

	const [isAlertOpen, setAlertIsOpen] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [locales, setLocales] = useState({});
	const { t } = useTranslation();

	const closeDialog = () => setAlertIsOpen(false);

	useEffect(() => {
		setTimeout(SplashScreen?.hide, 3000);
	}, []);

	// Locales / hint images are bundled statically — load on mount, no auth gate.
	// Previous behaviour (April 2026) waited on signInStore success; the license
	// flow has been removed so the app goes straight from the language picker
	// to /home. The Firebase code in services/signin.ts is left in place but
	// never invoked.
	useEffect(() => {
		loadLocals()
			.then((locales) => {
				setLocales(locales as any);
			})
			.catch((e) => console.log(e))
			.finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		if (Capacitor.isNativePlatform()) {
			CapApp.addListener("backButton", (e) => {
				const currentLocation = window.location.pathname;
				if (currentLocation === "/home") {
					setAlertIsOpen(true);
				} else {
					window.history.back();
				}
			});
		}
	}, []);

	return (
		<LocalizationContext.Provider value={locales}>
			<WithLoader isLoading={isLoading}>
				<IonApp>
					{/* Forced first-run language picker. Hides itself once
					    settings.lang is set in user.settings (localStorage). */}
					<LanguagesModal />
					<WithMenu>
						<IonAlert
							isOpen={isAlertOpen}
							cssClass="confirm-delete"
							header={t("exit.title")}
							message={t("exit.confirmText")}
							buttons={[
								{
									text: t("general.cancel"),
									role: "cancel",
									cssClass: "secondary",
									handler: closeDialog,
								},
								{
									text: t("general.ok"),
									handler: exitApp,
								},
							]}
						/>
						<IonRouterOutlet id="home-app">
							<Router>
								<Switch>
									<Route exact path="/home">
										<Home />
									</Route>
									<Route exact path="/create-system">
										<CreateSystem />
									</Route>
									<Route exact path="/edit-system/:id">
										<EditSystem />
									</Route>
									<Route exact path="/system/:id">
										<System />
									</Route>
									<Route exact path="/signin">
										{/* License flow disabled — old route redirects home. */}
										<Redirect to="/home" />
									</Route>
									<Route exact path="/settings">
										<Settings />
									</Route>
									<Route exact path="/about">
										<About />
									</Route>
									<Route exact path="/system/:systemId/propulsion">
										<Propulsion />
									</Route>
									<Route exact path="/system/:systemId/tow-cable">
										<TowCable />
									</Route>
									<Route exact path="/system/:systemId/trawl-gates">
										<TrawlGates />
									</Route>
									<Route exact path="/system/:systemId/gate-selection">
										<GateSelection />
									</Route>
									<Route exact path="/system/:systemId/resistance-expansion">
										<ResistanceAndExpansion />
									</Route>
									<Route exact path="/system/:systemId/guinches">
										<Guinches />
									</Route>
									<Route exact path="/system/:systemId/trawl-systems">
										<TrawlSystems />
									</Route>
									<Route exact path="/system/:systemId/demersals">
										<Demersals />
									</Route>
									<Route exact path="/system/:systemId/crustacean">
										<Crustacean />
									</Route>
									<Route exact path="/system/:systemId/triple-tow">
										<TripleTow />
									</Route>
									<Route exact path="/system/:systemId/double-tow">
										<DoubleTow />
									</Route>
									<Route exact path="/system/:systemId/pair">
										<Pair />
									</Route>
									<Route exact path="/system/:systemId/tangones">
										<Tangones />
									</Route>
									<Route exact path="/system/:systemId/flotation">
										<Flotation />
									</Route>
									<Route exact path="/system/:systemId/cloths">
										<Cloths />
									</Route>
									<Route exact path="/faq">
										<Faq />
									</Route>
									<Route exact path="/">
										<Redirect to="/home" />
									</Route>
								</Switch>
							</Router>
						</IonRouterOutlet>
					</WithMenu>
					<ErrorBoundary />
				</IonApp>
			</WithLoader>
		</LocalizationContext.Provider>
	);
};

export default App;
